import { Controller } from "@hotwired/stimulus"
import { circliful } from "js-plugin-circliful"

// Connects to data-controller="circliful"
// Initializes circliful on elements with .myStat class using the new npm module
// API docs: https://github.com/pguso/js-plugin-circliful/blob/master/docs/options.md
export default class extends Controller {
  static values = {
    selector: { type: String, default: ".myStat" }
  }

  connect() {
    // Initialize immediately - no need to wait for fade-in
    this.initializeCircliful()
  }

  initializeCircliful() {
    // Find all .myStat elements within this controller's element
    const elements = this.element.querySelectorAll(this.selectorValue)

    if (elements.length === 0) {
      return
    }

    // Initialize circliful for each element
    elements.forEach((element, index) => {
      this.initializeElement(element, index)
    })
  }

  initializeElement(element, index, retryCount = 0) {
    const maxRetries = 10

    // Generate unique ID if element doesn't have one
    let elementId = element.id
    if (!elementId) {
      elementId = `circliful-${Date.now()}-${index}`
      element.id = elementId
    }

    // Read data attributes
    const percent = parseFloat(element.getAttribute('data-percent')) || 0
    const text = element.getAttribute('data-text') || ''
    const dimension = parseInt(element.getAttribute('data-dimension')) || 110
    const foregroundColor = element.getAttribute('data-fgcolor') || '#556b2f'
    const backgroundColor = element.getAttribute('data-bgcolor') || '#eee'
    const fontSize = parseInt(element.getAttribute('data-fontsize')) || 20
    // data-width="1" is very thin (1px) - increase to minimum 10px for visibility
    const dataWidth = parseInt(element.getAttribute('data-width')) || 15
    const strokeWidth = Math.max(dataWidth, 10) // Minimum 10px for visibility

    // Check if element is visible first
    const computed = window.getComputedStyle(element)
    if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
      if (retryCount < maxRetries) {
        // Element is hidden, wait and retry
        setTimeout(() => {
          this.initializeElement(element, index, retryCount + 1)
        }, 200) // Increased retry delay
        return
      }
    }

    // Set element dimensions FIRST - the library reads element size via getBoundingClientRect()
    // This must be done before calling newCircle()
    element.style.width = `${dimension}px`
    element.style.height = `${dimension}px`
    element.style.position = 'relative'
    element.style.display = 'inline-block'
    element.style.overflow = 'visible'

    // Set CSS custom properties for colors (can be used by CSS if needed)
    element.style.setProperty('--foreground-color', foregroundColor)
    element.style.setProperty('--background-color', backgroundColor)

    // Wait for browser to recalculate layout after setting dimensions
    // The library needs the element to have computed dimensions
    requestAnimationFrame(() => {
      // Double-check element has dimensions before proceeding
      const rect = element.getBoundingClientRect()
      if ((rect.width === 0 || rect.height === 0) && retryCount < maxRetries) {
        // Retry after another frame
        requestAnimationFrame(() => {
          this.initializeElement(element, index, retryCount + 1)
        })
        return
      }

      if (rect.width === 0 || rect.height === 0) {
        console.error(`[circliful] Element ${elementId} still has no dimensions after ${maxRetries} retries`)
        return
      }

      // Ensure element has the exact dimensions we set (not 0)
      if (rect.width !== dimension || rect.height !== dimension) {
        // Force the dimensions
        element.style.width = `${dimension}px`
        element.style.height = `${dimension}px`
        // Wait for dimensions to be fully computed and stable
        setTimeout(() => {
          this.createCircle(element, elementId, percent, text, dimension, foregroundColor, backgroundColor, fontSize, strokeWidth)
        }, 200) // Increased delay to 200ms
        return
      }

      // Add delay to ensure dimensions are fully computed and stable
      setTimeout(() => {
        this.createCircle(element, elementId, percent, text, dimension, foregroundColor, backgroundColor, fontSize, strokeWidth)
      }, 200) // Increased delay to 200ms
    })
  }

  createCircle(element, elementId, percent, text, dimension, foregroundColor, backgroundColor, fontSize, strokeWidth) {
    try {
      // Build options object according to API docs
      // https://github.com/pguso/js-plugin-circliful/blob/master/docs/options.md
      const options = {
        id: elementId,
        percent: percent,
        type: 'simple',
        // API uses foregroundCircleWidth and backgroundCircleWidth
        foregroundCircleWidth: strokeWidth,
        backgroundCircleWidth: strokeWidth,
        // Enable animation for visual appeal
        animation: true,
        // Set stroke linecap to round for smoother appearance
        strokeLinecap: 'round'
      }

      // Add text if provided
      if (text) {
        options.text = text
        // Keep percentage visible, text shows below
        options.textReplacesPercentage = false
      }

      // Use progressColors to set foreground color
      // This is the API-supported way to set colors dynamically
      if (foregroundColor) {
        options.progressColors = [
          { percent: 0, color: foregroundColor }
        ]
      }

      // Create the circle - library will read element dimensions now
      const circleInstance = circliful.newCircle(options)

      // Apply additional custom styling after creation via DOM manipulation
      // Use a longer timeout to ensure SVG is fully created
      setTimeout(() => {
        const svg = element.querySelector('svg')
        if (svg) {
          // Ensure SVG size matches our dimension
          svg.setAttribute('width', dimension)
          svg.setAttribute('height', dimension)
          svg.setAttribute('viewBox', `0 0 ${dimension} ${dimension}`)
          svg.style.width = `${dimension}px`
          svg.style.height = `${dimension}px`

          // Style background circle (the library creates this)
          const backgroundCircle = svg.querySelector('.background-circle')
          if (backgroundCircle) {
            backgroundCircle.setAttribute('stroke', backgroundColor)
            backgroundCircle.setAttribute('fill', 'none')
            backgroundCircle.setAttribute('stroke-width', strokeWidth)
            // Force the color with inline style to override CSS
            backgroundCircle.style.stroke = backgroundColor
          }

          // Style foreground path (progress circle) - library may have set color via progressColors
          // But we override to ensure our color is used
          const foregroundPath = svg.querySelector('.foreground-circle')
          if (foregroundPath) {
            foregroundPath.setAttribute('stroke', foregroundColor)
            foregroundPath.setAttribute('fill', 'none')
            foregroundPath.setAttribute('stroke-width', strokeWidth)
            // Force the color with inline style to override CSS - this is critical!
            foregroundPath.style.stroke = foregroundColor

            // Check if path has NaN - if so, try to fix it
            const pathD = foregroundPath.getAttribute('d')
            if (pathD && pathD.includes('NaN')) {
              console.warn(`[circliful] Path has NaN for ${elementId}, attempting to fix...`)
              // The library might need to recalculate - try updating the circle
              if (circleInstance && typeof circleInstance.update === 'function') {
                circleInstance.update({ type: 'percent', value: percent })
              }
            }
          }

          // Style text elements
          const textElements = svg.querySelectorAll('.circle-text, .circle-info-text')
          textElements.forEach(textEl => {
            textEl.setAttribute('font-size', fontSize)
            textEl.setAttribute('fill', '#444')
            textEl.setAttribute('text-anchor', 'middle')
            textEl.setAttribute('dominant-baseline', 'middle')
          })

          // Hide the duplicate info text if we only want the main text
          const infoText = svg.querySelector('.circle-info-text')
          if (infoText && text) {
            const info = element.getAttribute('data-info')
            if (!info || info === '') {
              infoText.style.display = 'none'
            }
          }
        }
      }, 300) // Increased timeout to ensure SVG is fully created and path calculations are complete
    } catch (error) {
      console.error(`[circliful] Error initializing circle for ${elementId}:`, error)
    }
  }
}
